/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IAddressAddItem, IAddressAddUpdateProps } from '@msdyn365-commerce-modules/address/src/modules/checkout-shipping-address/../../common/components/address-add';
import { IAddressSelectItem, IAddressSelectProps } from '@msdyn365-commerce-modules/address/src/modules/checkout-shipping-address/../../common/components/address-select';
import { IAddressShowItem, IAddressShowProps } from '@msdyn365-commerce-modules/address/src/modules/checkout-shipping-address/../../common/components/address-show';
import { ICheckoutShippingAddressViewProps } from '@msdyn365-commerce-modules/address/src/modules/checkout-shipping-address/./checkout-shipping-address';
import { ICheckoutShippingAddressResources, ICheckoutShippingAddressConfig } from '../definition-extensions/checkout-shipping-address.ext.props.autogenerated';

export interface ICheckoutShippingAddressViewPropsExt extends Omit<ICheckoutShippingAddressViewProps,'config'> {
    resources: ICheckoutShippingAddressResources;
    config: ICheckoutShippingAddressConfig;
}

export interface IAddressAddUpdatePropsExt extends IAddressAddUpdateProps {
    config: ICheckoutShippingAddressConfig;
    resources: ICheckoutShippingAddressResources;
    channelCountryRegionISOCode?: string;
}

interface ICountryInput {
    key: string;
    value: string;
}

/**
 * Address show component.
 * @param param0 - Root param.
 * @param param0.AddressDetail - Address detail.
 * @param param0.items - IAddressShowItem[].
 * @returns - Address Node.
 */
const AddressShow: React.FC<IAddressShowProps> = ({ AddressDetail, items }) => {
    return (
        <Node {...AddressDetail}>
            {items.map((item: IAddressShowItem) => {
                return <>{item.description}</>;
            })}
        </Node>
    );
};

/**
 * Address Select Component.
 * @param param0 - Root param.
 * @param param0.SelectAddress - Select address.
 * @param param0.addButton - Add button.
 * @param param0.items - IAddressSelectItem[].
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns - SelectAddress Node.
 */
const AddressSelect: React.FC<IAddressSelectProps> = ({
    SelectAddress,
    addButton,
    items,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton
}) => {
    return (
        <Node {...SelectAddress}>
            {addButton}
            {items.map((item: IAddressSelectItem) => {
                const SelectItem = item.SelectItem;
                return (
                    <Node {...SelectItem} key={item.key}>
                        {item.input}
                        <AddressShow {...item.showItems} />
                    </Node>
                );
            })}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

const validateRequiredFields = () => {
    const saveAddressButton = document.querySelector('.ms-checkout__guided-card-btn-save.msc-btn') as HTMLButtonElement;
    document
        .getElementsByClassName('msc-address-form')[0]
        .querySelectorAll('input, select')
        .forEach(function(element) {
            (element as HTMLInputElement).onchange = () => {
                isRequiredIsFilled();
            };
        });
    const isRequiredIsFilled = () => {
        let allAreFilled = true;
        document
            .getElementsByClassName('msc-address-form')[0]
            .querySelectorAll("[aria-required = 'true']")
            .forEach(function(element) {
                if (!allAreFilled) {
                    return;
                }
                if (!(element as HTMLInputElement).value) {
                    allAreFilled = false;
                    return;
                }
            });
        if (allAreFilled && document.getElementsByClassName('address-form__item-invalid').length === 0) {
            saveAddressButton.removeAttribute('disabled');
        } else {
            saveAddressButton.setAttribute('disabled', 'true');
        }
    };
    if (document.getElementsByClassName('address-form__item-invalid').length > 0) {
        saveAddressButton.setAttribute('disabled', 'true');
    } else {
        isRequiredIsFilled();
    }
}
const checkPhone = (requiredErrorMessage: string) => {
    const phone = document.getElementById('shipping_addressphone') as HTMLInputElement;
    if (phone) {
        const phoneCtn = phone.closest('.msc-address-form__item') as HTMLElement;
        const phoneAlert = phoneCtn.getElementsByClassName('msc-address-form__alert')[0];

        phone.setAttribute('aria-required', 'true');
        phone.setAttribute('type', 'tel');
        phone.setAttribute('maxlength', '15');
        phone.onkeyup = () => {
            if (phone.value.length < 1) {
                phoneCtn.classList.add('address-form__item-invalid');
                phoneAlert.textContent = requiredErrorMessage;
            } else {
                phoneAlert.textContent === requiredErrorMessage &&
                    phoneCtn.classList.contains('address-form__item-invalid') &&
                    phoneCtn.classList.remove('address-form__item-invalid');
                if (phoneAlert.textContent === requiredErrorMessage) {
                    phoneAlert.textContent = '';
                }
            }
            validateRequiredFields();
        };
       
    }
};

const checkZipCode = (formatErrorMessage: string) => {
    const zipCode = document.getElementById('shipping_addresszipcode') as HTMLInputElement;
    if (zipCode) {
        const zipCodeCtn = zipCode.closest('.msc-address-form__item') as HTMLElement;
        const zipCodeAlert = zipCodeCtn.getElementsByClassName('msc-address-form__alert')[0];
        let zipCodeFormatAlert: any;
        if(document.getElementsByClassName('msc-address-form__alert format').length === 0){
            zipCodeFormatAlert = document.createElement('span');
            zipCodeFormatAlert.className = 'msc-address-form__alert format';
            zipCodeFormatAlert.textContent = formatErrorMessage;
            zipCodeAlert.after(zipCodeFormatAlert);
        }

        const zipRegex = /^(\d{5})?$/;
        zipCode.onchange = () => {
            if(!zipCode.value.match(zipRegex) || zipCode.value.length === 0) {
                zipCodeCtn.classList.add('address-form__item-invalid');
            }
        }
        zipCode.onkeyup = () => {
            if(!zipCode.value.match(zipRegex) || zipCode.value.length === 0) {
               zipCodeCtn.classList.add('address-form__item-invalid');
            } else {
                zipCodeCtn.classList.remove('address-form__item-invalid');
            }
            validateRequiredFields();
        };
    }
};

/**
 * Address Add Update Component.
 * @param param0 - Root param.
 * @param param0.AddressForm - Address form.
 * @param param0.heading - Address heading.
 * @param param0.items - IAddressAddItem[].
 * @param param0.hasError - Boolean.
 * @param param0.error - Error message.
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns Address add update component node.
 */
const AddressAddUpdate: React.FC<IAddressAddUpdatePropsExt> = ({
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton,
    resources,
    config,
    channelCountryRegionISOCode
}) => {
    React.useEffect(() => {
        checkPhone(resources.addressPhoneRequiredErrorMessage);
        validateRequiredFields();
    }, [(document?.getElementById('shipping_addressphone') as HTMLInputElement)?.value]);
    React.useEffect(() => {
        if(channelCountryRegionISOCode === 'IT') {
            checkZipCode(resources.invalidZipCodeAddressError);
            validateRequiredFields();
        }
    }, [(document?.getElementById('shipping_addresszipcode') as HTMLInputElement)?.value]);
    return (
        <Node {...AddressForm}>
            {heading}
            {items.map((item: IAddressAddItem) => {
                const { AddressItem, key, label, alert, input } = item;
                const inputC = React.cloneElement(input as React.ReactElement, {});
                if (key === 'ThreeLetterISORegionName') {
                    if (channelCountryRegionISOCode === 'US') {
                        const contriesFiltered = inputC.props.displayData.filter((item: ICountryInput) => item.key === 'USA');
                        inputC.props.displayData = contriesFiltered;
                    } else if (channelCountryRegionISOCode === 'IT') {
                        const contriesFiltered = inputC.props.displayData.filter(
                            (item: ICountryInput) => item.key === 'ITA' || item.key === 'SMR'
                        );
                        inputC.props.displayData = contriesFiltered;
                    }
                }
                return (
                    <Node {...AddressItem} key={key}>
                        {label}
                        {alert}
                        {inputC}
                    </Node>
                );
            })}
            {_isMilitaryCheck(config)}
            {hasError && (
                <Node {...error.AddressError}>
                    {error.title}
                    {error.message}
                </Node>
            )}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

const _isMilitaryCheck = (config: ICheckoutShippingAddressConfig) => {
    const isMilitaryVal = localStorage.getItem('is_military_address') === 'true' ? true : false;
    const [isMilitary, setIsMilitary] = React.useState(isMilitaryVal);
    const handleChange = () => {
        setIsMilitary(!isMilitary);
    };
    React.useEffect(() => {
        localStorage.setItem('is_military_address', String(isMilitary));
    }, [isMilitary]);
    if (!config.isMilitaryText || config.isMilitaryText === '') {
        localStorage.removeItem('is_military_address');
        return;
    }
    return (
        <div className={'msc-address-form__item msc-address-form__item-ismilitary'}>
            <label>
                <input type={'checkbox'} onChange={handleChange} checked={isMilitary} />
                <span>{config.isMilitaryText}</span>
            </label>
        </div>
    );
};

/**
 * Checkout Shipping Address View Component.
 * @param props - Props.
 * @returns - CheckoutShippingAddress Module.
 */
const CheckoutShippingAddressView: React.FC<ICheckoutShippingAddressViewPropsExt> = props => {
    const { CheckoutShippingAddress, viewState, showAddress, showAddressSelect, showAddOrUpdateAddress, cartLineImages } = props;
    const showAddOrUpdateAddressExt = {
        ...showAddOrUpdateAddress,
        config: props.config
    };
    const channelCountryRegionISOCode = props?.context?.request?.channel?.ChannelCountryRegionISOCode;
    return (
        <Module {...CheckoutShippingAddress}>
            {cartLineImages}
            {viewState.isShowAddress && <AddressShow {...showAddress} />}
            {viewState.isShowAddresList && <AddressSelect {...showAddressSelect} />}
            {viewState.isShowAddOrUpdateAddress && <AddressAddUpdate
                    resources={props.resources}
                    channelCountryRegionISOCode={channelCountryRegionISOCode}
                    {...showAddOrUpdateAddressExt}
                />}
        </Module>
    );
};

export default CheckoutShippingAddressView;
